import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';

import { ProjectModel } from '../../models/project.model';
import { ProjectActions } from './project.actions';

export interface ProjectState
  extends EntityState<ProjectModel.ProjectDocument> {
  isGettingProject: boolean;
  hasLoadedProject: boolean;
  selectedProject: ProjectModel.ProjectDocument | null;
  isLoading: boolean;
  hasLoaded: boolean;
  latestTs: number | null;
  isLastPage: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  closingProjectId: string | null;
  deletingProjectIds: string[];
  activeRequestFilters: ProjectModel.API.ListProjectsQuery | null;
}

export const projectAdapter: EntityAdapter<ProjectModel.ProjectDocument> =
  createEntityAdapter<ProjectModel.ProjectDocument>({
    selectId: (project) => project._id,
    sortComparer: (a, b) => b.createdAt - a.createdAt,
  });

const initialState: ProjectState = projectAdapter.getInitialState({
  isGettingProject: false,
  hasLoadedProject: false,
  selectedProject: null,
  isLoading: false,
  hasLoaded: false,
  latestTs: null,
  isLastPage: false,
  isCreating: false,
  isUpdating: false,
  closingProjectId: null,
  deletingProjectIds: [],
  activeRequestFilters: null,
});

export const projectStateReducer = createReducer(
  initialState,
  on(ProjectActions.resetToInitialState, () => {
    return {
      ...initialState,
    };
  }),
  on(ProjectActions.getProject, (state, { projectId }) => {
    return {
      ...state,
      isGettingProject: true,
      hasLoadedProject: false,
      requestedProjectId: projectId,
    };
  }),
  on(ProjectActions.getProjectSuccess, (state, { project }) => {
    return {
      ...state,
      isGettingProject: false,
      hasLoadedProject: true,
      selectedProject: project,
    };
  }),
  on(ProjectActions.getProjectFail, (state, { projectId }) => {
    return {
      ...state,
      isGettingProject: false,
      hasLoadedProject: true,
      requestedProjectId: projectId,
    };
  }),
  on(ProjectActions.setSelectedProject, (state, { project }) => {
    return { ...state, selectedProject: project };
  }),
  on(ProjectActions.beforeLoadProjects, (state, { filters }) => {
    return projectAdapter.removeAll({
      ...state,
      isLoading: true,
      hasLoaded: false,
      latestTs: null,
      isLastPage: false,
      activeRequestFilters: filters,
    });
  }),
  on(ProjectActions.loadProjects, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(ProjectActions.loadProjectsSuccess, (state, { projects, filters }) => {
    const totalLength = projects.length;
    const nextLatestTs = totalLength
      ? projects[totalLength - 1].createdAt
      : state.latestTs;
    const nextState = {
      ...state,
      isLoading: false,
      hasLoaded: true,
      latestTs: nextLatestTs,
      isLastPage: totalLength < ProjectModel.PAGE_LIMIT,
      activeRequestFilters: filters,
    };

    if (state.latestTs === null) {
      return projectAdapter.setAll(projects, nextState);
    }

    if (!totalLength) {
      return nextState;
    }

    return projectAdapter.addMany(projects, nextState);
  }),
  on(ProjectActions.loadProjectsFail, (state) => {
    return {
      ...state,
      isLoading: false,
      hasLoaded: true,
    };
  }),
  on(ProjectActions.createProject, (state) => {
    return {
      ...state,
      isCreating: true,
    };
  }),
  on(ProjectActions.createProjectSuccess, (state, { project }) => {
    const shouldAddProject = ProjectModel.matchesRequestFilters(
      project,
      state.activeRequestFilters,
    );

    return shouldAddProject
      ? projectAdapter.upsertOne(project, {
        ...state,
        isCreating: false,
      })
      : {
        ...state,
        isCreating: false,
      };
  }),
  on(ProjectActions.createProjectFail, (state) => {
    return {
      ...state,
      isCreating: false,
    };
  }),
  on(ProjectActions.updateProject, (state) => {
    return {
      ...state,
      isUpdating: true,
    };
  }),
  on(ProjectActions.updateProjectSuccess, (state, { project }) => {
    const shouldAddProject = ProjectModel.matchesRequestFilters(
      project,
      state.activeRequestFilters,
    );
    const nextState = {
      ...state,
      isUpdating: false,
      selectedProject: project,
    };

    return shouldAddProject
      ? projectAdapter.upsertOne(project, nextState)
      : projectAdapter.removeOne(project._id, nextState);
  }),
  on(ProjectActions.updateProjectFail, (state) => {
    return {
      ...state,
      isUpdating: false,
    };
  }),
  on(ProjectActions.closeProject, (state, { projectId }) => {
    return {
      ...state,
      closingProjectId: projectId,
    };
  }),
  on(ProjectActions.closeProjectSuccess, (state, { project }) => {
    const shouldAddProject = ProjectModel.matchesRequestFilters(
      project,
      state.activeRequestFilters,
    );
    const nextState = {
      ...state,
      selectedProject: project,
      closingProjectId:
        state.closingProjectId === project._id
          ? null
          : state.closingProjectId,
    };

    return shouldAddProject
      ? projectAdapter.upsertOne(project, nextState)
      : projectAdapter.removeOne(project._id, nextState);
  }),
  on(ProjectActions.closeProjectFail, (state, { projectId }) => {
    return {
      ...state,
      closingProjectId:
        state.closingProjectId === projectId
          ? null
          : state.closingProjectId,
    };
  }),
  on(ProjectActions.deleteProject, (state, { projectId }) => {
    return {
      ...state,
      deletingProjectIds: state.deletingProjectIds.includes(projectId)
        ? state.deletingProjectIds
        : [...state.deletingProjectIds, projectId],
    };
  }),
  on(ProjectActions.deleteProjectSuccess, (state, { projectId }) => {
    const selectedProject =
      state.selectedProject?._id === projectId ? null : state.selectedProject;

    return projectAdapter.removeOne(projectId, {
      ...state,
      selectedProject,
      deletingProjectIds: state.deletingProjectIds.filter(
        (id) => id !== projectId,
      ),
    });
  }),
  on(ProjectActions.deleteProjectFail, (state, { projectId }) => {
    return {
      ...state,
      deletingProjectIds: state.deletingProjectIds.filter(
        (id) => id !== projectId,
      ),
    };
  }),
);

export const projectFeature = createFeature({
  name: 'project',
  reducer: projectStateReducer,
  extraSelectors: ({ selectProjectState }) => {
    const { selectAll, selectEntities, selectIds, selectTotal } =
      projectAdapter.getSelectors(selectProjectState);

    return {
      selectProjectIds: selectIds,
      selectProjectEntities: selectEntities,
      selectProjects: selectAll,
      selectProjectsTotal: selectTotal,
    };
  },
});
